import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import { formatDate } from "../../config/formatters.ts";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface NewsItem {
  id: number;
  title: string;
  label: string;
  description: string;
  news_date: string;
  sort_order: number;
  status: boolean;
}

export default function News() {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getNews();
  }, [page, limit, search]);

  const getNews = async () => {
    setLoading(true);
    const response = await request.post("news", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setNews(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 180,
      format: (value) => value,
    },
    {
      id: "label",
      label: "Label",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "news_date",
      label: "News Date",
      minWidth: 140,
      format: (value) => formatDate(value),
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/news/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.title }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="News"
        showSearch
        searchPlaceholder="Search news..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add News",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/news/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={news}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete News - " + deleteDialog?.title}
          message="Are you sure you want to delete this news? This action cannot be undone."
          apiEndpoint={"news/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getNews();
          }}
        />
      </PageWrapper>
    </>
  );
}